import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
  UseQueryOptions,
  keepPreviousData,
} from "@tanstack/react-query";

import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { QData } from "../../../types/basic-type-list";
import { useState } from "react";
import { InsightPayload } from "@src/types/api/insight";
import { deleteInsight, listInsight, saveInsight } from "./callback";

export const useInsightSave = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: (res: TResponse<InsightPayload>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<InsightPayload>,
    unknown,
    InsightPayload
  > = {}
): UseMutationResult<TResponse<InsightPayload>, unknown, InsightPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<InsightPayload>, unknown, InsightPayload>({
    mutationFn: async (data) => {
      const res = await saveInsight(data);
      return res.data;
    },

    onSuccess: (res, variables, context, mutation) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null }],
        (old?: TResponseWithQData<InsightPayload[]>) => {
          if (!old || !res.data) return old;

          const exists = old.data.find((x) => x._id === res.data._id);

          return {
            ...old,
            data: exists
              ? old.data.map((x) =>
                  x._id === res.data!._id ? res.data! : x
                )
              : [res.data!, ...old.data],
          };
        }
      );

      onSuccess?.(res);
      options.onSuccess?.(res, variables, context, mutation);
    },

    onError: (err, variables, context, mutation) => {
      onError?.(err);
      options.onError?.(err, variables, context, mutation);
    },

    ...options,
  });
};


export const useInsightList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<InsightPayload[]>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponseWithQData<InsightPayload[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<InsightPayload[]>, Error>({
    queryKey: [API_ENDPOINTS.SERVICE_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await listInsight(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      qData: {
        ...queryParams,
        total: res.extra?.total,
      },
    }),
    placeholderData: keepPreviousData,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params) => {
      setQueryParams((prev) => ({ ...prev, ...params }));
    },
  };
};


export const useInsightDelete = (
  {
    qData = { page: 1, limit: 20 },
    onSuccess,
    onError,
  }: {
    qData?: QData;
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  } = {},
  options?: UseMutationOptions<
    TResponse<null>,
    unknown,
    { id: string | string[] }
  >
): UseMutationResult<
  TResponse<null>,
  unknown,
  { id: string | string[] }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const res = await deleteInsight(payload);
      return res.data;
    },

    onSuccess: (res, payload, context, mutation) => {
      queryClient.setQueryData(
        [API_ENDPOINTS.SERVICE_LIST, { ...qData, total: null }],
        (old?: TResponseWithQData<InsightPayload[]>) => {
          if (!old) return old;

          return {
            ...old,
            data: Array.isArray(payload.id)
              ? old.data.filter((x) => x._id && !payload.id.includes(x._id))
              : old.data.filter((x) => x._id !== payload.id),
          };
        }
      );

      onSuccess?.();
      options?.onSuccess?.(res, payload, context, mutation);
    },

    onError: (err, payload, context, mutation) => {
      onError?.(err);
      options?.onError?.(err, payload, context, mutation);
    },

    ...options,
  });
};